// Remote support — the peer connection, for BOTH roles (shared/REMOTE-SUPPORT.md §1, §3, §3.1).
//
//  requester: `offer(track)` → a NEW RTCPeerConnection with the screen as its one video track and the `rs`
//             data channel (created before the offer, ordered + reliable) → offer → signal. It offers again on
//             every helper `ready` (fresh connection, same track).
//  helper:    an `offer` → a NEW connection (an older one is closed) → answer → signal. The video arrives
//             in `ontrack`, the channel in `ondatachannel`. The helper never offers.
// Candidates trickle both ways; one that arrives before the remote description waits in a queue.
// Everything runs on one promise chain — interleaving setRemote/createAnswer is how signaling-state errors
// happen (callRtcSession.ts learned the same).
//
// Link watchdog (§1.7): `disconnected` / `failed` arms a 20 s timer, `connected` disarms it; when it fires
// the session is over (`ended('link')`). `closed` from the far end ends at once.
import type { CallIceServer } from '../core/callIceServer';
import { DATA_CHANNEL_LABEL, LINK_DROP_MS, type SignalPayload } from './protocol';

export type SupportRole = 'requester' | 'helper';

export interface SupportRtcListener {
  /** Send this payload to the other member (`POST /sessions/{id}/signal`). */
  signal(payload: SignalPayload): void;
  /** The `rs` channel opened (requester: send `hello`; both: `POST /active`). */
  channelOpen(): void;
  channelClosed(): void;
  message(text: string): void;
  /** Helper: the remote screen (null when the connection is replaced). */
  remoteStream(stream: MediaStream | null): void;
  /** RTCPeerConnection.connectionState, for the status line and the log. */
  connectionState(state: string): void;
  /** The link stayed down past LINK_DROP_MS, or failed for good. */
  ended(reason: 'link' | 'failed'): void;
  log?(line: string): void;
}

export interface RtcEnvironment {
  createPeerConnection(config: RTCConfiguration): RTCPeerConnection;
  later(fn: () => void, ms: number): () => void;
}

export const browserRtc: RtcEnvironment = {
  createPeerConnection: (config) => new RTCPeerConnection(config),
  later: (fn, ms) => {
    const id = setTimeout(fn, ms);
    return () => clearTimeout(id);
  },
};

export class SupportRtc {
  private pc: RTCPeerConnection | null = null;
  private dc: RTCDataChannel | null = null;
  private chain: Promise<unknown> = Promise.resolve();
  private pendingCandidates: RTCIceCandidateInit[] = [];
  private cancelDrop: (() => void) | null = null;
  private screen: { track: MediaStreamTrack; stream: MediaStream } | null = null;
  private closed = false;
  /** Bumped per connection: callbacks from a replaced one are ignored. */
  private generation = 0;

  constructor(
    readonly role: SupportRole,
    private readonly iceServers: CallIceServer[],
    private readonly listener: SupportRtcListener,
    private readonly env: RtcEnvironment = browserRtc,
  ) {}

  get isClosed(): boolean {
    return this.closed;
  }

  get channelOpen(): boolean {
    return this.dc?.readyState === 'open';
  }

  private run<T>(job: () => Promise<T> | T): Promise<T | undefined> {
    const next = this.chain.then(() => (this.closed ? undefined : job()));
    this.chain = next.catch((error) => this.listener.log?.(`rtc step failed: ${String(error)}`));
    return next.catch(() => undefined);
  }

  /** Requester: (re)offer the screen on a fresh connection. */
  offer(track: MediaStreamTrack, stream: MediaStream): Promise<unknown> {
    if (this.role !== 'requester') throw new Error('only the requester offers');
    this.screen = { track, stream };
    return this.run(async () => {
      const pc = this.newConnection();
      // Text and edges matter more than motion on a support screen.
      try {
        track.contentHint = 'detail';
      } catch {
        /* read-only in some engines */
      }
      const sender = pc.addTrack(track, stream);
      try {
        const params = sender.getParameters();
        (params as RTCRtpSendParameters & { degradationPreference?: string }).degradationPreference = 'maintain-resolution';
        await sender.setParameters(params);
      } catch {
        /* optional */
      }
      this.attachChannel(pc.createDataChannel(DATA_CHANNEL_LABEL, { ordered: true }));
      const offer = await pc.createOffer();
      if (pc !== this.pc) return;
      await pc.setLocalDescription(offer);
      this.listener.signal({ type: 'offer', sdp: pc.localDescription?.sdp ?? offer.sdp ?? '' });
    });
  }

  /** A payload from the other member. */
  handleSignal(payload: SignalPayload): Promise<unknown> {
    switch (payload.type) {
      case 'offer':
        if (this.role !== 'helper') return Promise.resolve();
        return this.run(async () => {
          const pc = this.newConnection();
          await pc.setRemoteDescription({ type: 'offer', sdp: payload.sdp });
          await this.flushCandidates(pc);
          const answer = await pc.createAnswer();
          if (pc !== this.pc) return;
          await pc.setLocalDescription(answer);
          this.listener.signal({ type: 'answer', sdp: pc.localDescription?.sdp ?? answer.sdp ?? '' });
        });
      case 'answer':
        if (this.role !== 'requester') return Promise.resolve();
        return this.run(async () => {
          const pc = this.pc;
          if (!pc || pc.signalingState !== 'have-local-offer') return;
          await pc.setRemoteDescription({ type: 'answer', sdp: payload.sdp });
          await this.flushCandidates(pc);
        });
      case 'candidate':
        return this.run(async () => {
          const init: RTCIceCandidateInit = { candidate: payload.candidate, sdpMid: payload.sdpMid || null, sdpMLineIndex: payload.sdpMLineIndex };
          const pc = this.pc;
          if (!pc || !pc.remoteDescription) {
            this.pendingCandidates.push(init);
            return;
          }
          await pc.addIceCandidate(init).catch((error) => this.listener.log?.(`candidate refused: ${String(error)}`));
        });
      case 'ready':
        if (this.role !== 'requester' || !this.screen) return Promise.resolve();
        this.listener.log?.('helper ready: offering again');
        return this.offer(this.screen.track, this.screen.stream);
    }
  }

  /** Helper: ask the requester for a fresh offer (§3.1 `ready`). */
  sendReady(): void {
    if (this.role === 'helper') this.listener.signal({ type: 'ready' });
  }

  /** One data-channel message. False when the channel is not open (the caller drops it). */
  send(text: string): boolean {
    const dc = this.dc;
    if (!dc || dc.readyState !== 'open') return false;
    try {
      dc.send(text);
      return true;
    } catch {
      return false;
    }
  }

  /** Requester: swap the shared screen without renegotiating (a new display picked mid-session). */
  async replaceTrack(track: MediaStreamTrack, stream: MediaStream): Promise<void> {
    this.screen = { track, stream };
    const sender = this.pc?.getSenders().find((s) => s.track?.kind === 'video' || s.track === null);
    if (sender) await sender.replaceTrack(track);
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    this.disarmDrop();
    this.teardown();
  }

  private newConnection(): RTCPeerConnection {
    this.teardown();
    const generation = ++this.generation;
    // Signals arrive in order (SignalQueue), so a candidate queued now belongs to the connection being replaced.
    this.pendingCandidates = [];
    const pc = this.env.createPeerConnection({
      iceServers: this.iceServers.map((s) => ({ urls: s.urls, username: s.username, credential: s.credential })),
      iceTransportPolicy: 'all',
      bundlePolicy: 'max-bundle',
    });
    this.pc = pc;
    const current = () => generation === this.generation && !this.closed;
    pc.onicecandidate = (e) => {
      if (!current() || !e.candidate || !e.candidate.candidate) return;
      this.listener.signal({ type: 'candidate', candidate: e.candidate.candidate, sdpMid: e.candidate.sdpMid ?? '', sdpMLineIndex: e.candidate.sdpMLineIndex ?? 0 });
    };
    pc.onconnectionstatechange = () => {
      if (!current()) return;
      const state = pc.connectionState;
      this.listener.connectionState(state);
      if (state === 'connected') this.disarmDrop();
      else if (state === 'disconnected' || state === 'failed') this.armDrop(state === 'failed' ? 'failed' : 'link');
      else if (state === 'closed') this.listener.ended('link');
    };
    pc.ontrack = (e) => {
      if (!current() || this.role !== 'helper') return;
      const stream = e.streams[0] ?? new MediaStream([e.track]);
      this.listener.remoteStream(stream);
    };
    pc.ondatachannel = (e) => {
      if (!current() || this.role !== 'helper' || e.channel.label !== DATA_CHANNEL_LABEL) return;
      this.attachChannel(e.channel);
    };
    return pc;
  }

  private attachChannel(dc: RTCDataChannel): void {
    this.dc = dc;
    const generation = this.generation;
    const current = () => generation === this.generation && !this.closed;
    dc.onopen = () => current() && this.listener.channelOpen();
    dc.onclose = () => current() && this.listener.channelClosed();
    dc.onmessage = (e) => {
      if (current() && typeof e.data === 'string') this.listener.message(e.data);
    };
    if (dc.readyState === 'open') queueMicrotask(() => current() && this.listener.channelOpen());
  }

  private async flushCandidates(pc: RTCPeerConnection): Promise<void> {
    const queued = this.pendingCandidates;
    this.pendingCandidates = [];
    for (const c of queued) await pc.addIceCandidate(c).catch((error) => this.listener.log?.(`queued candidate refused: ${String(error)}`));
  }

  private armDrop(reason: 'link' | 'failed'): void {
    if (this.cancelDrop) return;
    this.listener.log?.(`link ${reason === 'failed' ? 'failed' : 'down'}: ending in ${LINK_DROP_MS / 1000} s unless it recovers`);
    this.cancelDrop = this.env.later(() => {
      this.cancelDrop = null;
      if (!this.closed) this.listener.ended(reason);
    }, LINK_DROP_MS);
  }

  private disarmDrop(): void {
    if (this.cancelDrop) this.cancelDrop();
    this.cancelDrop = null;
  }

  private teardown(): void {
    const dc = this.dc;
    const pc = this.pc;
    this.dc = null;
    this.pc = null;
    this.generation++;
    if (this.role === 'helper' && pc) this.listener.remoteStream(null);
    try {
      dc?.close();
    } catch {
      /* already closed */
    }
    try {
      pc?.close();
    } catch {
      /* already closed */
    }
  }
}
